"use client"

import {
    Box,
    Container,
    Heading,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    useColorModeValue,
} from "@chakra-ui/react"
import { motion, useInView } from "framer-motion"
import { useRef } from "react"

const MotionBox = motion(Box)

const faqs = [
    {
        question: "How does the candidate matching algorithm work?",
        answer:
            "Our proprietary algorithm analyzes over 50 data points from both job descriptions and candidate profiles to create optimal matches. It considers skills, experience, education, career trajectory, and even soft skills to ensure you find candidates who are not just qualified, but also a great fit for your company culture.",
    },
    {
        question: "What makes your platform different from other job boards?",
        answer:
            "Unlike traditional job boards, we offer a comprehensive hiring ecosystem with AI-powered matching, candidate screening tools, and a vast database of pre-vetted professionals. Our focus is on quality over quantity, saving you time by connecting you with candidates who truly match your requirements.",
    },
    {
        question: "How much does it cost to post a job?",
        answer:
            "We offer flexible pricing plans starting from $199 per month for small businesses. Our enterprise solutions are customized based on hiring volume and specific requirements. All plans include access to our candidate database, screening tools, and applicant tracking system.",
    },
    {
        question: "Can I try the platform before committing to a subscription?",
        answer:
            "We offer a 14-day free trial that gives you full access to all features. You can post jobs, search for candidates, and test our screening tools without any commitment. No credit card is required to start your trial.",
    },
    {
        question: "How do you ensure candidate data is up-to-date?",
        answer:
            "We use a combination of automated verification systems and regular prompts for candidates to update their profiles. Our platform also integrates with professional networks to sync recent experience and skill updates, ensuring you always see the most current information.",
    },
]

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
    },
}

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" },
    },
}

export default function FAQSection() {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true, amount: 0.1 })

    const bgPrimary = useColorModeValue("white", "gray.900")
    const textPrimary = useColorModeValue("gray.800", "white")
    const textSecondary = useColorModeValue("gray.600", "gray.300")

    return (
        <Box as="section" py={{ base: 16, md: 24 }} bg={bgPrimary} className="position-relative">
            <Container maxW="container.xl">
                <MotionBox
                    ref={ref}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={fadeIn}
                    textAlign="center"
                    mb={16}
                    className="animate-fadeInUp"
                >
                    <Heading as="h2" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={textPrimary} mb={4}>
                        Frequently Asked{" "}
                        <Text
                            as="span"
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-transparent bg-clip-text inline"
                        >
                            Questions
                        </Text>
                    </Heading>
                    <Text fontSize={{ base: "lg", md: "xl" }} color={textSecondary} maxW="3xl" mx="auto">
                        Find answers to common questions about our platform and services.
                    </Text>
                </MotionBox>

                <MotionBox
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={scaleIn}
                    maxW="4xl"
                    mx="auto"
                    className="animate-scaleIn"
                >
                    <Accordion allowToggle>
                        {faqs.map((faq, index) => (
                            <AccordionItem
                                key={index}
                                borderWidth="1px"
                                mb={4}
                                borderRadius="md"
                                overflow="hidden"
                                as={motion.div}
                                whileHover={{ boxShadow: "md" }}
                                className="hover-lift"
                            >
                                <h2>
                                    <AccordionButton
                                        py={4}
                                        _expanded={{ bg: "indigo.50", color: "indigo.600" }}
                                        transition="all 0.2s"
                                        _hover={{ bg: "gray.50" }}
                                    >
                                        <Box flex="1" textAlign="left" fontWeight="medium">
                                            {faq.question}
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4} bg="gray.50">
                                    <Text color={textSecondary}>{faq.answer}</Text>
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </MotionBox>
            </Container>
        </Box>
    )
}
